import { type FormEvent, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  addNote,
  getTicket,
  signTicketWaiver,
  updateTicket,
  updateTicketStatus,
  type TicketNote,
  type TicketRepair,
  type WaiverCollectionMode,
} from '@/lib/api/tickets.js';
import { createWarrantyTicket } from '@/lib/api/warranty.js';
import { WarrantyTicketBanner } from '@/components/tickets/WarrantyTicketBanner.js';
import { getCurrentStore } from '@/lib/api/stores.js';
import { formatTicketStatus } from '@/components/tickets/StatusBadge.js';
import { formatCurrency, formatDateTime } from '@/lib/format.js';
import { listUsers } from '@/lib/api/users.js';
import { createPartsRequest, listPartsRequests } from '@/lib/api/parts-requests.js';
import { listSuppliers } from '@/lib/api/suppliers.js';
import { SlideOver } from '@/components/SlideOver.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { clearRunningTimer, readRunningTimer, writeRunningTimer } from '@/lib/running-timer-storage.js';
import { TicketDetailRepairView } from '@/components/ticket-detail/TicketDetailRepairView.js';
import { TicketPaymentsPanel } from '@/components/payments/TicketPaymentsPanel.js';
import { WaiverModal } from '@/components/tickets/WaiverModal.js';
import { CollectDepositModal } from '@/components/tickets/CollectDepositModal.js';
import { depositRemaining } from '@/lib/deposit.js';
import { DeleteTicketModal } from '@/components/tickets/DeleteTicketModal.js';
import { useAuthStore } from '@/stores/auth.js';
import { usePermissions } from '@/hooks/usePermissions.js';
import { useManagerOverride } from '@/hooks/useManagerOverride.js';
import { ManagerOverrideModal } from '@/components/common/ManagerOverrideModal.js';
import { checkPermission } from '@/lib/permissions.js';
import { getPredictiveAlerts } from '@/lib/predictive-alerts-storage.js';
import type { PredictiveAlert } from '@/lib/api/tickets.js';
import { useUserPreferences } from '@/contexts/UserPreferencesContext.js';
import { prefersTechTicketView } from '@/lib/ticket-detail-path.js';
import { updateMyPreferences } from '@/lib/api/profile.js';
import { wsManager } from '@/lib/websocket-manager.js';

function initials(note: TicketNote) {
  return note.userId ? note.userId.slice(0, 2).toUpperCase() : 'FF';
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'rounded-xl px-4 py-2 text-sm font-semibold',
        active ? 'bg-primary text-white' : 'text-slate-600 hover:bg-slate-100',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const { preferences, isLoading: prefsLoading, applyPreferencesLocally } = useUserPreferences();
  const { flags } = usePermissions();
  const { requireOverride, modalProps: managerOverrideModal } = useManagerOverride();
  const [tab, setTab] = useState<'overview' | 'notes' | 'activity' | 'time'>('overview');
  const [noteBody, setNoteBody] = useState('');
  const [noteVisibility, setNoteVisibility] = useState<'public' | 'private'>('private');
  const [partsRepair, setPartsRepair] = useState<TicketRepair | null>(null);
  const [partName, setPartName] = useState('');
  const [partSupplierId, setPartSupplierId] = useState('');
  const [partQty, setPartQty] = useState('1');
  const [partUrgency, setPartUrgency] = useState<'normal' | 'rush' | 'urgent'>('normal');
  const [partNotes, setPartNotes] = useState('');
  const [setWaitingParts, setSetWaitingParts] = useState(true);
  const [warrantyConfirmOpen, setWarrantyConfirmOpen] = useState(false);
  const [warrantyReason, setWarrantyReason] = useState('');
  const [warrantyRepairIds, setWarrantyRepairIds] = useState<string[]>([]);
  const [waiverModal, setWaiverModal] = useState<'pre_repair' | 'post_repair' | null>(null);
  const [depositModalOpen, setDepositModalOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [predictiveAlerts, setPredictiveAlerts] = useState<PredictiveAlert[]>([]);

  const ticketQuery = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => getTicket(id!),
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (!id) return;
    setPredictiveAlerts(getPredictiveAlerts(id));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const channel = `ticket:${id}`;
    wsManager.connect(channel, `/ws/ticket/${id}`);
    const unsub = wsManager.on(channel, (data) => {
      if (
        data.type === 'ticket.updated' ||
        data.type === 'ticket.status_changed' ||
        data.type === 'queue.update' ||
        data.type === 'ticket.waiver_signed'
      ) {
        queryClient.invalidateQueries({ queryKey: ['ticket', id] });
        queryClient.invalidateQueries({ queryKey: ['ticket-waiver-record', id] });
        queryClient.invalidateQueries({ queryKey: ['parts-requests', id] });
      }
    });
    return () => {
      unsub();
      wsManager.disconnect(channel);
    };
  }, [id, queryClient]);

  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: () => listUsers(),
  });

  const partsRequestsQuery = useQuery({
    queryKey: ['parts-requests', id],
    queryFn: () => listPartsRequests({ ticketId: id! }),
    enabled: Boolean(id),
  });

  const suppliersQuery = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => listSuppliers(),
  });

  const storeQuery = useQuery({
    queryKey: ['store-current'],
    queryFn: getCurrentStore,
  });

  useEffect(() => {
    const st = location.state as { openPreWaiver?: boolean } | undefined;
    if (st?.openPreWaiver && id) {
      setWaiverModal('pre_repair');
      navigate(`/tickets/${id}`, { replace: true, state: {} });
    }
  }, [location.state, id, navigate]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['ticket', id] });
    queryClient.invalidateQueries({ queryKey: ['parts-requests', id] });
    queryClient.invalidateQueries({ queryKey: ['tickets'] });
    queryClient.invalidateQueries({ queryKey: ['payments', 'ticket', id] });
  };

  const statusMutation = useMutation({
    mutationFn: (args: {
      status: string;
      managerOverrideAuthorizedBy?: string;
      clearStatusOverride?: boolean;
    }) =>
      updateTicketStatus(id!, {
        status: args.status,
        sendNotification: true,
        ...(args.clearStatusOverride ? { clearStatusOverride: true } : {}),
        ...(args.managerOverrideAuthorizedBy
          ? { managerOverrideAuthorizedBy: args.managerOverrideAuthorizedBy }
          : {}),
      }),
    onSuccess: (result, args) => {
      toastSuccess(`Status updated to ${formatTicketStatus(args.status)}`);
      if (result.startedTimer && id) {
        const repair =
          ticketQuery.data?.repairs.find((r) => r.id === result.startedTimer!.ticketRepairId) ??
          ticketQuery.data?.repairs[0];
        writeRunningTimer({
          ticketId: id,
          ticketNumber: result.ticket.ticketNumber,
          entryId: result.startedTimer.id,
          ...(result.startedTimer.ticketRepairId || repair?.id
            ? { repairId: result.startedTimer.ticketRepairId ?? repair!.id }
            : {}),
          startedAt: result.startedTimer.startedAt,
          repairName: repair?.serviceName || 'General',
        });
      } else if (args.status !== 'in_repair' && id) {
        const rt = readRunningTimer();
        if (rt?.ticketId === id) clearRunningTimer();
      }
      invalidate();
    },
    onError: (error) => {
      if (axios.isAxiosError(error) && error.response?.status === 422) {
        const payload = error.response.data as {
          errors?: Array<{ code?: string; message?: string; outstanding?: number }>;
        };
        const first = payload.errors?.[0];
        if (first?.code === 'DEPOSIT_REQUIRED' && first.outstanding != null) {
          toastError(
            `${first.message} (${formatCurrency(Number(first.outstanding))} outstanding)`,
            first.code,
          );
          return;
        }
      }
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  async function handleStatusChange(status: string) {
    statusMutation.mutate({ status });
  }

  async function handleVoidTicket() {
    const ticket = ticketQuery.data?.ticket;
    if (!ticket) return;
    if (!window.confirm(`Void ticket ${ticket.ticketNumber}? This cannot be undone.`)) return;

    const perm = checkPermission({
      flags,
      userRole: user?.role ?? 'read_only',
      flag: 'void_ticket',
    });
    let managerOverrideAuthorizedBy: string | undefined;
    if (!perm.allowed) {
      const result = await requireOverride({
        action: 'void_ticket',
        title: 'Manager Approval to Void Ticket',
        description: `Voiding ${ticket.ticketNumber} (${formatCurrency(ticket.total)})`,
        context: { ticketId: ticket.id, amount: Number(ticket.total) },
      });
      if (!result.approved) return;
      managerOverrideAuthorizedBy = result.authorizedBy.id;
    }
    statusMutation.mutate({
      status: 'voided',
      ...(managerOverrideAuthorizedBy ? { managerOverrideAuthorizedBy } : {}),
    });
  }

  const ticketMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => updateTicket(id!, body),
    onSuccess: (_data, body) => {
      if (body.preWaiverSkipped) toastSuccess('Pre-repair waiver skipped');
      else toastSuccess('Ticket updated');
      invalidate();
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const signWaiverMutation = useMutation({
    mutationFn: (args: {
      waiverType: 'pre_repair' | 'post_repair';
      signatureDataUrl: string;
      waiverVersion: string;
      collectionMode: WaiverCollectionMode;
    }) => signTicketWaiver(id!, args),
    onSuccess: () => {
      toastSuccess('Signature saved');
      setWaiverModal(null);
      invalidate();
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const noteMutation = useMutation({
    mutationFn: () => addNote(id!, { body: noteBody, visibility: noteVisibility, pinned: false }),
    onSuccess: () => {
      setNoteBody('');
      toastSuccess('Note added');
      invalidate();
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const partsRequestMutation = useMutation({
    mutationFn: () =>
      createPartsRequest({
        ticketId: id!,
        ticketRepairId: partsRepair?.id,
        partName,
        supplierId: partSupplierId || undefined,
        quantityNeeded: Number(partQty || 1),
        urgency: partUrgency,
        supplierNotes: partNotes || undefined,
      }),
    onSuccess: () => {
      if (setWaitingParts) statusMutation.mutate({ status: 'waiting_parts' });
      setPartsRepair(null);
      setPartName('');
      setPartSupplierId('');
      setPartQty('1');
      setPartUrgency('normal');
      setPartNotes('');
      toastSuccess('Parts request submitted');
      invalidate();
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const warrantyMutation = useMutation({
    mutationFn: () =>
      createWarrantyTicket(id!, {
        warrantyReason: warrantyReason.trim(),
        repairIds: warrantyRepairIds,
      }),
    onSuccess: (created) => {
      const ticketNumber = String(created.ticketNumber ?? '');
      const createdId = String(created.id ?? '');
      toastSuccess(`Warranty ticket ${ticketNumber} created`);
      setWarrantyConfirmOpen(false);
      navigate(`/tickets/${createdId}`);
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const detail = ticketQuery.data;
  const ticket = detail?.ticket;
  const customer = detail?.customer;
  const customerName = customer
    ? [customer.firstName, customer.lastName].filter(Boolean).join(' ')
    : (ticket?.customerNameOverride ??
      ticket?.customerPhoneOverride ??
      ticket?.customerEmailOverride ??
      'Customer');
  const allowSkipPre = Boolean(
    storeQuery.data?.preWaiverSkippableForReturning &&
      customer &&
      Number(customer.totalTickets ?? 0) > 1,
  );
  const defaultWaiverMode = (storeQuery.data?.waiverCollectionMode ?? 'hand_across') as WaiverCollectionMode;
  const sortedNotes = useMemo(
    () =>
      [...(detail?.notes ?? [])].sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt)),
    [detail?.notes],
  );

  if (!prefsLoading && id && prefersTechTicketView(user?.role, preferences?.ticketDetailView)) {
    return <Navigate to={`/tech/tickets/${id}${location.search}`} replace />;
  }

  if (ticketQuery.isLoading) {
    return (
      <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading ticket...</div>
    );
  }

  if (!detail || !ticket) {
    return (
      <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Ticket not found.</div>
    );
  }

  function submitNote(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (noteBody.trim()) noteMutation.mutate();
  }

  function openPartsRequest(repair: TicketRepair) {
    setPartsRepair(repair);
    setPartName(repair.serviceName);
  }

  const partsRequests = partsRequestsQuery.data ?? [];
  const isPosSale = ticket.source === 'pos_sale' || ticket.ticketNumber.startsWith('SALE-');

  if (isPosSale) {
    const displayCustomerName =
      customerName === 'Customer' || customerName === 'Walk-in' ? 'Walk-in' : customerName;
    const saleItems = detail.lineItems.filter((line) => line.type !== 'discount');
    const cartDiscountLines = detail.lineItems.filter((line) => line.type === 'discount');
    const itemDiscountTotal = saleItems.reduce(
      (sum, line) => sum + Number(line.discountAmount ?? 0),
      0,
    );
    const cartDiscountTotal = cartDiscountLines.reduce(
      (sum, line) => sum + Math.abs(Number(line.lineTotal ?? line.discountAmount ?? 0)),
      0,
    );
    const taxableAmount = Math.max(Number(ticket.total ?? 0) - Number(ticket.taxTotal ?? 0), 0);
    const youSaved = itemDiscountTotal + cartDiscountTotal;
    const saleTotalsInput: Array<[string, string | number]> = [
      ['Subtotal', ticket.subtotal],
      ['Item discounts', `-${itemDiscountTotal}`],
      ['Cart discount', `-${cartDiscountTotal}`],
      ['Taxable amount', taxableAmount],
      ['Tax', ticket.taxTotal],
      ['Total', ticket.total],
      ['Paid', ticket.paidTotal],
    ];
    const saleTotals = saleTotalsInput.filter(([label]) => {
      if (label === 'Item discounts') return itemDiscountTotal > 0;
      if (label === 'Cart discount') return cartDiscountTotal > 0;
      return true;
    });

    return (
      <div className="space-y-6">
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Sale Receipt
              </p>
              <h1 className="mt-1 text-3xl font-black text-slate-950">{ticket.ticketNumber}</h1>
              <p className="mt-2 text-sm text-slate-500">
                {formatDateTime(ticket.createdAt)} · {displayCustomerName}
              </p>
            </div>
            <span className="rounded-full bg-green-100 px-4 py-2 text-sm font-bold text-green-700">
              {formatTicketStatus(ticket.status)} · Paid
            </span>
          </div>
        </section>

        <div className="flex flex-wrap gap-2 rounded-2xl bg-white p-2 shadow-sm ring-1 ring-slate-200">
          <TabButton active={tab === 'overview'} onClick={() => setTab('overview')}>
            Receipt
          </TabButton>
          <TabButton active={tab === 'notes'} onClick={() => setTab('notes')}>
            Notes
          </TabButton>
        </div>

        {tab === 'overview' ? (
          <div className="grid gap-6 xl:grid-cols-[1fr_360px]">
            <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-5 py-4">
                <h2 className="text-lg font-semibold text-slate-950">Items Sold</h2>
              </div>
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-5 py-3">Item</th>
                    <th className="px-5 py-3">Qty</th>
                    <th className="px-5 py-3">Unit Price</th>
                    <th className="px-5 py-3 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {saleItems.map((line) => (
                    <tr key={line.id}>
                      <td className="px-5 py-4 font-medium text-slate-900">
                        {line.description}
                        {Number(line.discountAmount ?? 0) > 0 ? (
                          <div className="mt-1 text-xs font-semibold text-green-700">
                            Discount: -{formatCurrency(line.discountAmount)}
                            {Number(line.discountPct ?? 0) > 0 ? ` (${line.discountPct}% off)` : ''}
                            {line.discountReason ? ` · ${line.discountReason}` : ''}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-5 py-4 text-slate-600">{line.quantity}</td>
                      <td className="px-5 py-4 text-slate-600">{formatCurrency(line.unitPrice)}</td>
                      <td className="px-5 py-4 text-right font-semibold text-slate-950">
                        {formatCurrency(line.lineTotal)}
                      </td>
                    </tr>
                  ))}
                  {cartDiscountLines.map((line) => (
                    <tr key={line.id} className="bg-green-50/60">
                      <td className="px-5 py-4 font-semibold text-green-800">{line.description}</td>
                      <td className="px-5 py-4 text-green-700">1</td>
                      <td className="px-5 py-4 text-green-700">Discount</td>
                      <td className="px-5 py-4 text-right font-semibold text-green-800">
                        {formatCurrency(line.lineTotal)}
                      </td>
                    </tr>
                  ))}
                  {detail.lineItems.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-5 py-8 text-center text-sm text-slate-500">
                        No sale items found.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </section>

            <aside className="space-y-6">
              <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <TicketPaymentsPanel
                  ticketId={ticket.id}
                  ticketNumber={ticket.ticketNumber}
                  customerName={displayCustomerName}
                  paidTotal={ticket.paidTotal}
                  balanceDue={ticket.balanceDue}
                  ticketTotal={ticket.total}
                  initialPayments={detail.payments}
                  onAddPayment={
                    Number(ticket.balanceDue ?? 0) > 0
                      ? () => navigate(`/tickets/${ticket.id}/checkout`)
                      : undefined
                  }
                  onChanged={invalidate}
                />
              </section>

              <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-950">Totals</h2>
                <div className="mt-4 space-y-3 text-sm">
                  {saleTotals.map(([label, value]) => (
                    <div key={label} className="flex justify-between">
                      <span className="text-slate-500">{label}</span>
                      <span
                        className={[
                          'font-semibold',
                          label.includes('discount') ? 'text-green-700' : 'text-slate-950',
                        ].join(' ')}
                      >
                        {formatCurrency(value)}
                      </span>
                    </div>
                  ))}
                  {youSaved > 0 ? (
                    <div className="flex justify-between rounded-lg bg-green-50 px-3 py-2 font-bold text-green-700">
                      <span>You saved</span>
                      <span>{formatCurrency(youSaved)}</span>
                    </div>
                  ) : null}
                </div>
              </section>
            </aside>
          </div>
        ) : null}

        {tab === 'notes' ? (
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <form className="mb-6 space-y-3" onSubmit={submitNote}>
              <textarea
                className="min-h-24 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                placeholder="Add a note..."
                value={noteBody}
                onChange={(e) => setNoteBody(e.target.value)}
              />
              <div className="flex items-center justify-between">
                <div className="flex rounded-xl bg-slate-100 p-1">
                  {(['private', 'public'] as const).map((visibility) => (
                    <button
                      key={visibility}
                      type="button"
                      onClick={() => setNoteVisibility(visibility)}
                      className={[
                        'rounded-lg px-3 py-1.5 text-sm font-semibold capitalize',
                        noteVisibility === visibility
                          ? 'bg-white text-slate-950 shadow-sm'
                          : 'text-slate-500',
                      ].join(' ')}
                    >
                      {visibility}
                    </button>
                  ))}
                </div>
                <button
                  className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
                  disabled={noteMutation.isPending}
                >
                  Add Note
                </button>
              </div>
            </form>
            <div className="space-y-4">
              {sortedNotes.map((note) => (
                <div key={note.id} className="flex gap-3 rounded-xl border border-slate-200 p-4">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">
                    {initials(note)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <span
                        className={[
                          'rounded-full px-2 py-0.5 text-xs font-semibold uppercase',
                          note.visibility === 'public'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-slate-100 text-slate-700',
                        ].join(' ')}
                      >
                        {note.visibility}
                      </span>
                      <span className="text-xs text-slate-500">
                        {formatDateTime(note.createdAt)}
                      </span>
                    </div>
                    <p className="whitespace-pre-wrap text-sm text-slate-700">{note.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {ticket.clonedFrom ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-950 shadow-sm">
          <p className="font-medium">
            Cloned from {ticket.clonedFrom.ticketNumber}
          </p>
          <Link
            to={`/tickets/${ticket.clonedFrom.id}`}
            className="shrink-0 rounded-xl bg-sky-700 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-800"
          >
            View Original
          </Link>
        </div>
      ) : null}
      {ticket.depositRequired &&
      depositRemaining(ticket.depositAmount, ticket.depositCollected) > 0 &&
      !ticket.depositSkipped ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 shadow-sm ring-1 ring-amber-100">
          <p className="min-w-0 flex-1 font-medium">
            <span aria-hidden>⚠ </span>
            Deposit Required — {formatCurrency(depositRemaining(ticket.depositAmount, ticket.depositCollected))}{' '}
            not yet collected. Collect before starting work.
          </p>
          <button
            type="button"
            className="shrink-0 rounded-xl bg-amber-700 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-800"
            onClick={() => setDepositModalOpen(true)}
          >
            Collect Deposit →
          </button>
        </div>
      ) : null}

      <WarrantyTicketBanner ticket={ticket} />

      <TicketDetailRepairView
        detail={detail}
        users={usersQuery.data ?? []}
        partsRequests={partsRequests}
        customerName={customerName}
        predictiveAlerts={predictiveAlerts}
        onDismissPredictiveAlerts={() => setPredictiveAlerts([])}
        onStatusChange={(status) => handleStatusChange(status)}
        onClearOverride={() =>
          statusMutation.mutate({ status: ticket.status, clearStatusOverride: true })
        }
        onSwitchToBench={() => {
          applyPreferencesLocally({ ticketDetailView: 'tech' });
          void updateMyPreferences({ ticketDetailView: 'tech' });
          navigate(`/tech/tickets/${ticket.id}`);
        }}
        onPriorityChange={(priority) => ticketMutation.mutate({ priority })}
        onCheckout={() => navigate(`/tickets/${ticket.id}/checkout`)}
        onCreateWarranty={() => {
          setWarrantyRepairIds((detail?.repairs ?? []).map((repair) => repair.id));
          setWarrantyReason('');
          setWarrantyConfirmOpen(true);
        }}
        onCollectDeposit={() => setDepositModalOpen(true)}
        onOrderPart={openPartsRequest}
        onRefetch={invalidate}
        onCollectPreWaiver={() => setWaiverModal('pre_repair')}
        onCollectPostWaiver={() => setWaiverModal('post_repair')}
        onVoid={() => {
          void handleVoidTicket();
        }}
        onDelete={() => setDeleteOpen(true)}
        canVoid={ticket.status !== 'voided' && ticket.status !== 'cancelled'}
        canDelete={user?.role === 'tenant_admin' || user?.role === 'platform_admin'}
      />

      <SlideOver
        title="Order Part"
        open={Boolean(partsRepair)}
        onClose={() => setPartsRepair(null)}
      >
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            partsRequestMutation.mutate();
          }}
        >
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Part name</span>
            <input
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={partName}
              onChange={(e) => setPartName(e.target.value)}
              required
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Supplier</span>
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={partSupplierId}
              onChange={(e) => setPartSupplierId(e.target.value)}
            >
              <option value="">No supplier selected</option>
              {suppliersQuery.data?.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Quantity needed</span>
            <input
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              type="number"
              min="1"
              value={partQty}
              onChange={(e) => setPartQty(e.target.value)}
            />
          </label>
          <div>
            <span className="text-sm font-medium text-slate-700">Urgency</span>
            <div className="mt-2 flex gap-2">
              {(['normal', 'rush', 'urgent'] as const).map((urgency) => (
                <button
                  key={urgency}
                  type="button"
                  onClick={() => setPartUrgency(urgency)}
                  className={[
                    'rounded-xl px-3 py-2 text-sm font-semibold capitalize',
                    partUrgency === urgency
                      ? 'bg-primary text-white'
                      : 'bg-slate-100 text-slate-600',
                  ].join(' ')}
                >
                  {urgency}
                </button>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Notes</span>
            <textarea
              className="mt-1 min-h-24 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={partNotes}
              onChange={(e) => setPartNotes(e.target.value)}
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={setWaitingParts}
              onChange={(e) => setSetWaitingParts(e.target.checked)}
            />
            Move ticket to Waiting Parts after submitting
          </label>
          <button
            className="w-full rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            disabled={!partName.trim() || partsRequestMutation.isPending}
          >
            {partsRequestMutation.isPending ? 'Submitting...' : 'Submit Request'}
          </button>
        </form>
      </SlideOver>

      {warrantyConfirmOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <form
            className="w-full max-w-lg space-y-4 rounded-2xl bg-white p-6 shadow-2xl"
            onSubmit={(event) => {
              event.preventDefault();
              warrantyMutation.mutate();
            }}
          >
            <h3 className="text-lg font-semibold text-slate-950">Create warranty ticket</h3>
            <p className="text-sm text-slate-600">
              Copies the customer, device, and selected repairs from {ticket.ticketNumber} at $0.
              The warranty end date stays the original date.
            </p>
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium text-slate-700">Repairs to cover</legend>
              {(detail?.repairs ?? []).map((repair) => (
                <label key={repair.id} className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={warrantyRepairIds.includes(repair.id)}
                    onChange={(event) =>
                      setWarrantyRepairIds((current) =>
                        event.target.checked
                          ? [...current, repair.id]
                          : current.filter((id) => id !== repair.id),
                      )
                    }
                  />
                  {repair.serviceName}
                </label>
              ))}
            </fieldset>
            <label className="block text-sm font-medium text-slate-700">
              Why is this coming back?
              <textarea
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={warrantyReason}
                onChange={(event) => setWarrantyReason(event.target.value)}
                required
              />
            </label>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
                onClick={() => setWarrantyConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-xl bg-violet-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                disabled={warrantyMutation.isPending || !warrantyReason.trim() || warrantyRepairIds.length === 0}
              >
                {warrantyMutation.isPending ? 'Creating…' : 'Create warranty ticket'}
              </button>
            </div>
          </form>
        </div>
      ) : null}
      <WaiverModal
        open={waiverModal !== null}
        onClose={() => setWaiverModal(null)}
        ticketId={ticket.id}
        waiverType={waiverModal ?? 'pre_repair'}
        defaultCollectionMode={defaultWaiverMode}
        allowSkip={waiverModal === 'pre_repair' && allowSkipPre}
        onSkip={() => ticketMutation.mutate({ preWaiverSkipped: true })}
        onDisplaySignatureComplete={async () => {
          await queryClient.invalidateQueries({ queryKey: ['ticket', ticket.id] });
        }}
        onSigned={async (r) => {
          if (!waiverModal) return;
          await signWaiverMutation.mutateAsync({ ...r, waiverType: waiverModal });
        }}
      />
      {ticket ? (
        <CollectDepositModal
          open={depositModalOpen}
          ticketId={ticket.id}
          ticketNumber={ticket.ticketNumber}
          maxAmount={depositRemaining(ticket.depositAmount, ticket.depositCollected)}
          customerHasStoreCredit={Number(customer?.storeCreditBalance ?? 0) > 0}
          onClose={() => setDepositModalOpen(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['ticket', ticket.id] });
            queryClient.invalidateQueries({ queryKey: ['tickets'] });
            setDepositModalOpen(false);
          }}
        />
      ) : null}
      {ticket ? (
        <DeleteTicketModal
          open={deleteOpen}
          ticket={ticket}
          {...(customer || ticket.customerNameOverride
            ? {
                customerName: customer
                  ? [customer.firstName, customer.lastName].filter(Boolean).join(' ')
                  : (ticket.customerNameOverride ?? null),
              }
            : {})}
          deviceSummary={
            detail.devices
              .map((d) => d.customModelName)
              .filter(Boolean)
              .join(' + ') || null
          }
          repairSummary={detail.repairs.map((r) => r.serviceName).join(', ') || null}
          onClose={() => setDeleteOpen(false)}
          onDeleted={() => {
            void queryClient.invalidateQueries({ queryKey: ['tickets'] });
            void queryClient.invalidateQueries({ queryKey: ['ticket-counts'] });
            navigate('/tickets');
          }}
        />
      ) : null}
      {managerOverrideModal ? <ManagerOverrideModal {...managerOverrideModal} /> : null}
    </div>
  );
}
